import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import ProductCard, { ProductCardSkeleton } from "@/components/storefront/ProductCard";
import { EmptyState, PageHead, ErrorNotice } from "@/components/storefront/common";
import { SlidersHorizontal, X, PackageSearch } from "lucide-react";

const SORTS = [
  ["relevance", "Relevance"], ["newest", "Newest"], ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"], ["popular", "Popular"], ["discount", "Discount"],
];

export default function Shop({ mode }) {
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const q = sp.get("q") || "";
  const sort = sp.get("sort") || "relevance";
  // Page state lives in the URL (`?page=N`) so pagination is shareable and the
  // fetch below always runs for the page the user selected. Invalid/absent
  // values fall back to page 1.
  const pageParam = Number.parseInt(sp.get("page") || "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const catFilter = mode === "category" ? params.slug : sp.get("category") || (mode === undefined && params.category) || "";
  const brandFilter = mode === "brand" ? params.slug : sp.get("brand") || "";
  const priceMax = sp.get("maxPrice") || "";
  const inStock = sp.get("inStock") === "true";

  useEffect(() => {
    // Filter lists are non-critical: report the failure but keep the catalogue usable.
    api.get("/categories").then(({ data }) => setCats(data.categories))
      .catch((err) => console.error("Shop filter categories unavailable:", err?.message));
    api.get("/brands").then(({ data }) => setBrands(data.brands))
      .catch((err) => console.error("Shop filter brands unavailable:", err?.message));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const qs = new URLSearchParams();
    if (q) qs.set("search", q);
    if (catFilter) qs.set("category", catFilter);
    if (brandFilter) qs.set("brand", brandFilter);
    if (priceMax) qs.set("maxPrice", priceMax);
    if (inStock) qs.set("inStock", "true");
    if (sp.get("newArrival")) qs.set("newArrival", "true");
    if (sp.get("bestSeller")) qs.set("bestSeller", "true");
    qs.set("sort", sort);
    qs.set("page", page);
    qs.set("limit", "12");
    api.get(`/products?${qs.toString()}`).then(({ data }) => {
      const totalPages = Math.max(1, Number(data.pages) || 1);
      if (page > totalPages) {
        // The requested page no longer exists (e.g. filters/search shrank the
        // result set): drop back to page 1 instead of showing an empty grid.
        const reset = new URLSearchParams(sp);
        reset.delete("page");
        setSp(reset);
        return;
      }
      setProducts(data.products);
      setMeta({ total: data.total, pages: totalPages });
    }).catch((err) => setLoadError(apiError(err)))
      .finally(() => setLoading(false));
  }, [q, catFilter, brandFilter, priceMax, inStock, sort, page, sp, setSp]);

  useEffect(() => { load(); }, [load]);

  const title = mode === "search" ? `Search: "${q}"` : mode === "category"
    ? cats.find((c) => c.slug === catFilter)?.name || "Category"
    : mode === "brand" ? brands.find((b) => b.slug === brandFilter)?.name || "Brand" : "All Products";

  // Filter/search/sort changes must reset pagination to page 1 — hence the
  // explicit `page` removal here (goToPage below is what *sets* the page).
  const update = (key, val) => {
    const n = new URLSearchParams(sp);
    if (val) n.set(key, val); else n.delete(key);
    n.delete("page");
    setSp(n);
  };
  const clearFilters = () => setSp(mode === "search" ? { q } : {});

  // Pagination: only ever writes the `page` param, so search, filters, sort,
  // category/brand state stay exactly as they are.
  const goToPage = (target) => {
    const next = Math.max(1, Number(target) || 1);
    const n = new URLSearchParams(sp);
    if (next === 1) n.delete("page"); else n.set("page", String(next));
    setSp(n);
    window.scrollTo(0, 0);
  };

  const Filters = () => (
    <div className="space-y-6" data-testid="shop-filters">
      {mode !== "category" && (
        <div>
          <h4 className="font-semibold text-sm text-slate-900 mb-2">Category</h4>
          <div className="space-y-1.5">
            {cats.map((c) => (
              <button key={c.id} onClick={() => update("category", catFilter === c.slug ? "" : c.slug)}
                className={`block text-sm text-left ${catFilter === c.slug ? "text-amber-600 font-semibold" : "text-slate-600 hover:text-slate-900"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {mode !== "brand" && (
        <div>
          <h4 className="font-semibold text-sm text-slate-900 mb-2">Brand</h4>
          <div className="space-y-1.5">
            {brands.map((b) => (
              <button key={b.id} onClick={() => update("brand", brandFilter === b.slug ? "" : b.slug)}
                className={`block text-sm text-left ${brandFilter === b.slug ? "text-amber-600 font-semibold" : "text-slate-600 hover:text-slate-900"}`}>
                {b.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <h4 className="font-semibold text-sm text-slate-900 mb-2">Max Price</h4>
        <input type="range" min="299" max="5000" step="100" value={priceMax || 5000}
          onChange={(e) => update("maxPrice", e.target.value === "5000" ? "" : e.target.value)}
          className="w-full accent-amber-500" data-testid="price-filter" />
        <p className="text-xs text-slate-500 mt-1">Up to ₹{priceMax || "5,000"}</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={inStock} onChange={(e) => update("inStock", e.target.checked ? "true" : "")} className="accent-amber-500 h-4 w-4" data-testid="instock-filter" />
        In stock only
      </label>
      <button onClick={clearFilters} className="text-sm font-medium text-slate-500 hover:text-amber-600" data-testid="clear-filters">Clear all filters</button>
    </div>
  );

  return (
    <div className="container-x py-8">
      <PageHead title={title} crumbs={[{ label: "Home", to: "/" }, { label: "Shop" }]} />

      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-sm text-slate-500" data-testid="product-count">{loading ? "Loading…" : loadError ? "Products unavailable" : `${meta.total} products`}</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setFiltersOpen(true)} className="lg:hidden inline-flex items-center gap-2 text-sm font-medium border border-slate-200 rounded-lg px-3 py-2" data-testid="open-filters-btn">
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
          <select value={sort} onChange={(e) => update("sort", e.target.value)} data-testid="sort-select"
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-amber-400">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="hidden lg:block"><Filters /></aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 9 }).map((_, i) => <ProductCardSkeleton key={i} />)}
            </div>
          ) : loadError ? (
            <ErrorNotice title="We couldn't load the product catalogue" text={loadError} onRetry={() => { setLoadError(null); load(); }} />
          ) : products.length === 0 ? (
            <EmptyState icon={PackageSearch} title="No products found" text="Try adjusting your filters or search for something else.">
              <button onClick={clearFilters} className="bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-lg">Clear filters</button>
            </EmptyState>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {meta.pages > 1 && (
                <nav className="flex justify-center gap-2 mt-10" aria-label="Product pages" data-testid="shop-pagination">
                  {Array.from({ length: meta.pages }).map((_, i) => {
                    const n = i + 1;
                    const active = n === page;
                    return (
                      <button key={n} type="button" onClick={() => goToPage(n)} disabled={active}
                        aria-label={`Go to page ${n}`} aria-current={active ? "page" : undefined}
                        data-testid={`shop-page-${n}`}
                        className={`h-10 w-10 rounded-lg text-sm font-medium ${active ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:border-amber-400"}`}>
                        {n}
                      </button>
                    );
                  })}
                </nav>
              )}
            </>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85%] bg-white p-5 overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-lg">Filters</h3>
              <button onClick={() => setFiltersOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <Filters />
            <button onClick={() => setFiltersOpen(false)} className="w-full mt-6 bg-slate-900 text-white font-semibold py-3 rounded-lg">Show {meta.total} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
